import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  ShieldAlert,
  Users,
  Car,
  Camera,
  Gauge,
  Flame,
  PhoneCall,
  Radio,
  Network,
  Router,
  Siren,
  Headphones,
  Truck,
  Globe,
  ArrowUpRight,
  CheckCircle2,
  Sparkles,
  Send,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { PARTNER_BRANDS, partnerStrings } from "../lib/partnersData";
import { QuoteModal } from "./QuoteModal";

const FEATURED_LABEL = {
  pt: "Solução em destaque",
  en: "Featured solution",
  es: "Solución destacada",
};

const KEY_FEATURES_LABEL = {
  pt: "Funcionalidades-chave",
  en: "Key features",
  es: "Funcionalidades clave",
};

const pickLocalized = (map, lang) => map[lang] || map.pt;

const ICON_MAP = {
  shield: ShieldCheck,
  "shield-alert": ShieldAlert,
  door: Users,
  car: Car,
  camera: Camera,
  dashboard: Gauge,
  fire: Flame,
  phone: PhoneCall,
  antenna: Radio,
  network: Network,
  router: Router,
  siren: Siren,
  headphones: Headphones,
  truck: Truck,
  globe: Globe,
};

const ProductCard = ({
  product,
  lang,
  accent,
  theme,
  imageFolder,
  onRequestQuote,
  ctaLabel,
  brandShortName,
}) => {
  const label = product[lang] || product.pt;
  const [errored, setErrored] = useState(false);
  return (
    <article
      data-testid={`product-${product.slug}`}
      className={`group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 ${theme.productHover} hover:shadow-lg`}
    >
      <button
        type="button"
        onClick={onRequestQuote}
        aria-label={`${ctaLabel} — ${label}`}
        className="relative block aspect-[4/3] w-full overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 text-left"
      >
        {!errored ? (
          <img
            src={`${imageFolder}/${product.slug}.png`}
            alt={label}
            loading="lazy"
            onError={() => setErrored(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Sparkles className="h-10 w-10 text-slate-300" />
          </div>
        )}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/10 to-transparent"
        />
        <span
          aria-hidden
          className={`pointer-events-none absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider opacity-0 shadow-sm transition-opacity group-hover:opacity-100 ${theme.productPreviewPill}`}
        >
          <Send className="h-3 w-3" strokeWidth={2.5} /> {ctaLabel}
        </span>
      </button>
      <div className="flex flex-1 flex-col p-4">
        <h5 className="font-heading text-sm font-semibold leading-snug text-slate-900 md:text-base">
          {label}
        </h5>
        <div className="mt-3 flex items-center justify-between gap-2">
          <div
            className={`flex items-center gap-1.5 text-xs font-medium ${theme.productBadge}`}
            style={{ color: accent }}
          >
            <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span>{brandShortName}</span>
          </div>
          <button
            type="button"
            onClick={onRequestQuote}
            data-testid={`quote-${product.slug}`}
            className={`inline-flex items-center gap-1 rounded-full border bg-white px-2.5 py-1 text-[11px] font-semibold transition-all ${theme.productQuoteCta}`}
          >
            {ctaLabel}
            <ArrowUpRight className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </article>
  );
};

const Tab = ({ active, onClick, Icon, label, accent, theme }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`tab-${label}`}
    className={`group inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
      active
        ? "border-transparent text-white shadow-md"
        : `border-slate-200 bg-white text-slate-700 ${theme.tabHover}`
    }`}
    style={active ? { background: accent } : undefined}
  >
    {Icon && <Icon className="h-4 w-4" strokeWidth={2.2} />}
    <span>{label}</span>
  </button>
);

export const BrandShowcase = ({ brandId }) => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const s = partnerStrings[lang] || partnerStrings.pt;
  const brand = PARTNER_BRANDS.find((b) => b.id === brandId);
  const [activeGroup, setActiveGroup] = useState(brand?.groups?.[0]?.id);
  const [quoteProduct, setQuoteProduct] = useState(null);

  // Deep-link support: hash like "#motorola:apx-portable" selects the
  // matching tab and scrolls to it. Updates when the hash changes.
  useEffect(() => {
    if (!brand) return;
    const applyHash = () => {
      const raw = window.location.hash.slice(1);
      if (!raw) return;
      const [bId, gId] = raw.split(":");
      if (bId !== brand.id || !gId) return;
      if (!brand.groups.find((g) => g.id === gId)) return;
      setActiveGroup(gId);
      requestAnimationFrame(() => {
        const el = document.querySelector(
          `[data-testid="${brand.id}-group-${gId}"]`,
        );
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, [brand]);

  if (!brand) return null;

  const theme = brand.theme;
  const why = brand.why[lang] || brand.why.pt;
  const group = brand.groups.find((g) => g.id === activeGroup) || brand.groups[0];
  const shortName = brand.name.split(/\s+/)[0];

  return (
    <section
      id={brand.id}
      data-testid={`showcase-${brand.id}`}
      className={`relative scroll-mt-24 overflow-hidden border-t border-slate-200 bg-gradient-to-b py-20 lg:py-24 ${theme.bgGradient}`}
    >
      <div
        aria-hidden
        className={`pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full blur-3xl ${theme.blob1}`}
      />
      <div
        aria-hidden
        className={`pointer-events-none absolute -right-32 bottom-10 h-80 w-80 rounded-full blur-3xl ${theme.blob2}`}
      />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <div
              className={`font-mono text-[11px] uppercase tracking-[0.28em] ${theme.overlineText}`}
            >
              {s.details_title} · {shortName}
            </div>
            <h2
              data-testid={`${brand.id}-title`}
              className="font-heading mt-4 max-w-3xl text-3xl font-black tracking-tight text-slate-900 md:text-5xl"
            >
              {brand.tagline[lang] || brand.tagline.pt}
            </h2>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                {s.pillars_label}:
              </span>
              {(brand.pillars[lang] || brand.pillars.pt).map((p) => (
                <span
                  key={p}
                  className={`rounded-full border bg-white px-3 py-1 text-xs font-bold uppercase tracking-wider ${theme.pillarPill}`}
                >
                  {p}
                </span>
              ))}
            </div>
          </div>

          <Link
            to="/#contact"
            data-testid={`${brand.id}-cta-proposal`}
            className={`inline-flex items-center gap-2 self-start rounded-full px-6 py-3 text-sm font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg md:self-end ${theme.ctaPrimary}`}
          >
            {s.cta_proposal}
            <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
          </Link>
        </div>

        {/* Segments */}
        <div className="mt-14">
          <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-slate-500">
            {s.segments_label}
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
            {(brand.segments[lang] || brand.segments.pt).map((seg) => (
              <div
                key={seg.name}
                data-testid={`${brand.id}-segment-${seg.name}`}
                className={`group rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${theme.segmentHover}`}
                title={seg.desc}
              >
                <div className="font-heading text-sm font-bold text-slate-900">
                  {seg.name}
                </div>
                <p className="mt-1 line-clamp-3 text-[11px] leading-snug text-slate-500">
                  {seg.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Solutions Tabs + Grid */}
        <div className="mt-16">
          <div className="max-w-3xl">
            <h3 className="font-heading text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
              {s.solutions_title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600 md:text-base">
              {s.solutions_subtitle}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-2" data-testid={`${brand.id}-tabs`}>
            {brand.groups.map((g) => {
              const Icon = ICON_MAP[g.icon] || ShieldCheck;
              return (
                <Tab
                  key={g.id}
                  active={activeGroup === g.id}
                  onClick={() => setActiveGroup(g.id)}
                  Icon={Icon}
                  accent={brand.color}
                  theme={theme}
                  label={g.title[lang] || g.title.pt}
                />
              );
            })}
          </div>

          <div
            key={group.id}
            className={`mt-8 rounded-3xl border bg-white/70 p-6 backdrop-blur-sm md:p-8 ${
              group.featured
                ? "border-2 shadow-xl"
                : "border-slate-200"
            }`}
            style={
              group.featured ? { borderColor: `${brand.color}66` } : undefined
            }
            data-testid={`${brand.id}-group-${group.id}`}
          >
            {group.featured && (
              <div
                className="mb-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white"
                style={{ background: brand.color }}
              >
                <Sparkles className="h-3 w-3" strokeWidth={2.5} />
                {pickLocalized(FEATURED_LABEL, lang)}
              </div>
            )}
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="max-w-2xl">
                <h4 className="font-heading text-xl font-bold text-slate-900 md:text-2xl">
                  {group.title[lang] || group.title.pt}
                </h4>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {group.description[lang] || group.description.pt}
                </p>
              </div>
              <Link
                to="/#contact"
                data-testid={`${brand.id}-cta-${group.id}`}
                className={`inline-flex h-10 shrink-0 items-center gap-2 self-start rounded-full border bg-white px-4 text-xs font-semibold transition-all ${theme.ctaOutline}`}
              >
                {s.cta_interested}
                <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.5} />
              </Link>
            </div>

            {group.specs && (
              <div
                className="mt-6 grid grid-cols-1 gap-3 rounded-2xl border border-dashed bg-slate-50/70 p-5 sm:grid-cols-2 lg:grid-cols-3"
                style={{ borderColor: `${brand.color}44` }}
                data-testid={`${brand.id}-group-${group.id}-specs`}
              >
                {(group.specs[lang] || group.specs.pt).map((spec) => (
                  <div
                    key={spec}
                    className="flex items-start gap-2 text-sm font-medium text-slate-800"
                  >
                    <CheckCircle2
                      className={`mt-0.5 h-4 w-4 shrink-0 ${theme.whyCheck}`}
                      strokeWidth={2.5}
                    />
                    {spec}
                  </div>
                ))}
              </div>
            )}

            {group.video && (
              <div
                className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-black shadow-lg"
                data-testid={`${brand.id}-group-${group.id}-video`}
              >
                <div className="relative aspect-video w-full">
                  <iframe
                    src={`https://www.youtube.com/embed/${group.video}?rel=0&modestbranding=1`}
                    title={`${group.title[lang] || group.title.pt} — vídeo`}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="absolute inset-0 h-full w-full"
                  />
                </div>
              </div>
            )}

            {group.highlights && (
              <div
                className="mt-5"
                data-testid={`${brand.id}-group-${group.id}-highlights`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  {pickLocalized(KEY_FEATURES_LABEL, lang)}
                </div>
                <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {(group.highlights[lang] || group.highlights.pt).map((h) => (
                    <li
                      key={h}
                      className="flex items-start gap-2 text-xs text-slate-700"
                    >
                      <span
                        aria-hidden
                        className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ background: brand.color }}
                      />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {group.products.map((p) => (
                <ProductCard
                  key={`${group.id}-${p.slug}`}
                  product={p}
                  lang={lang}
                  accent={brand.color}
                  theme={theme}
                  imageFolder={brand.imageFolder}
                  ctaLabel={s.card_quote_cta}
                  brandShortName={shortName}
                  onRequestQuote={() => {
                    const label = p[lang] || p.pt || p.slug;
                    const params = new URLSearchParams({
                      produto: label,
                      marca: shortName,
                    });
                    navigate(`/?${params.toString()}#contact`);
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Why this brand */}
        <div
          className={`mt-16 rounded-3xl border bg-gradient-to-br p-8 md:p-10 ${theme.whyPanel}`}
        >
          <h3 className="font-heading text-xl font-bold text-slate-900 md:text-2xl">
            {why.title}
          </h3>
          <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {why.items.map((item) => (
              <li
                key={item}
                className={`flex items-start gap-3 rounded-xl border bg-white p-4 text-sm text-slate-800 ${theme.whyItemBorder}`}
              >
                <CheckCircle2
                  className={`mt-0.5 h-5 w-5 shrink-0 ${theme.whyCheck}`}
                  strokeWidth={2.5}
                />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <Link
              to="/#contact"
              data-testid={`${brand.id}-cta-why`}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-slate-800"
            >
              {s.cta_proposal}
              <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </div>

      <QuoteModal
        open={Boolean(quoteProduct)}
        onClose={() => setQuoteProduct(null)}
        product={quoteProduct}
        brand={shortName}
        imageFolder={brand.imageFolder}
        theme={theme}
      />
    </section>
  );
};

export default BrandShowcase;
